import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useEffect, useState, type FC, type ReactNode } from "react";
import Text from "@/components/Text";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExportFrequency, type Grid, type RestaurantDetails } from "@/API";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { updateGrid } from "@/store/updateGridState";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

type Props = {
  children: ReactNode;
  grid: Grid | undefined;
  restaurants: RestaurantDetails[] | null;
  isUpdatingGrid: boolean;
  updatedGrid: boolean;
};

const PopoverPlanificationExport: FC<Props> = ({
  children,
  grid,
  restaurants,
  isUpdatingGrid,
  updatedGrid,
}) => {
  const { t } = useTranslation();
  const [exportFrequency, setExportFrequency] = useState<ExportFrequency>(
    ExportFrequency.ONE_WEEK,
  );
  const [emailInput, setEmailInput] = useState<string>("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (updatedGrid && open) {
      toast.success(t("export_scheduled_successfully"), {
        duration: 2000,
      });
      setOpen(false);
    }
  }, [updatedGrid]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent className="w-[330px] mr-[40px] mt-[8px]">
        <div className="flex flex-col gap-[24px]">
          <div className="flex flex-col gap-[12px]">
            <Text fontSize={16} fontWeight={500} color="#414651">
              {t("cadence")}
            </Text>
            <Select
              value={exportFrequency}
              onValueChange={(value) =>
                setExportFrequency(value as ExportFrequency)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectItem value={ExportFrequency.ONE_WEEK}>
                    {t("weekly")}
                  </SelectItem>
                  <SelectItem value={ExportFrequency.ONE_MONTH}>
                    {t("monthly")}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-[12px]">
            <Text fontSize={16} fontWeight={500} color="#414651">
              {t("recipients")}
            </Text>
            <div className="flex flex-col gap-[8px]">
              <Input
                placeholder="hello1@gmail.com,hello2@gmail.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
              <Text fontWeight={400} fontSize={14} color="#44546F">
                {t("export_recipients_helper_text")}
              </Text>
            </div>
          </div>

          <div className="flex gap-[12px] w-[100%]">
            <Button variant="secondaryFilled" onClick={() => setOpen(false)}>
              {t("cancel")}
            </Button>
            <Button
              disabled={isUpdatingGrid}
              style={{ width: "calc(100% - 94px)" }}
              onClick={() => {
                if (grid && restaurants) {
                  updateGrid({
                    grid: {
                      ...grid,
                      frequency: exportFrequency,
                      emails: emailInput
                        .split(",")
                        .map((email) => email.trim()),
                    },
                    restaurantsId: restaurants.map(
                      (restaurant) => restaurant.id,
                    ),
                  });
                }
              }}
            >
              {t("schedule_the_sending")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PopoverPlanificationExport;
